import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';
import { FaFileImport, FaTimes, FaBox } from 'react-icons/fa';
import ProductImport from './ProductImport';
import { theme, t } from '../theme';
import Tooltip from './Tooltip';
import { custoParaFormulario } from './etiquetaValidade';

const ProductForm = ({ produtoEditado, fecharModal }) => {
    const [produto, setProduto] = useState({
        nome: '', codigo_barras: '', codigo_barras_alternativo: '',
        unidade_peso: 'kg', peso_embalagem: '', estoque_minimo: '', lead_time_dias: '0',
        controla_estoque: true, custo_medio: '', estoque_inicial: '', observacoes: ''
    });
    const [mostrarModalImportacao, setMostrarModalImportacao] = useState(false);
    const [erro, setErro] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    // Chegou de um scan que nao achou o produto (/produtos?codigo=EAN): abre o
    // cadastro com o codigo ja preenchido. Sem isso a usuaria escaneia, ve
    // "nao cadastrado", e tem que digitar 13 digitos a mao — que e exatamente
    // onde ela desiste.
    useEffect(() => {
        const codigo = searchParams.get('codigo');
        if (!codigo || produtoEditado) return;
        setProduto(prev => (prev.codigo_barras ? prev : { ...prev, codigo_barras: codigo }));
        searchParams.delete('codigo');
        setSearchParams(searchParams, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, setSearchParams, produtoEditado]);

    // Máscara: digite da esquerda pra direita; resultado sempre com 3 casas decimais (0,000).
    const formatPesoEmbalagemInput = (raw) => {
        const digits = String(raw).replace(/\D/g, '');
        if (!digits) return '';
        const num = parseInt(digits, 10) / 1000;
        return num.toFixed(3).replace('.', ',');
    };

    useEffect(() => {
        if (produtoEditado) {
            setProduto({
                ...produtoEditado,
                lead_time_dias: produtoEditado.lead_time_dias || '0',
                controla_estoque: produtoEditado.controla_estoque !== false,
                custo_medio: custoParaFormulario(produtoEditado.custo_medio ?? produtoEditado.custo_fixo),
                estoque_inicial: '',
                observacoes: produtoEditado.observacoes || '',
                peso_embalagem: produtoEditado.peso_embalagem != null && produtoEditado.peso_embalagem !== ''
                    ? Number(produtoEditado.peso_embalagem).toFixed(3).replace('.', ',')
                    : '',
            });
        }
    }, [produtoEditado]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setProduto({ ...produto, [name]: type === 'checkbox' ? checked : value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErro(null);
        try {
            const controla = !!produto.controla_estoque;
            const dados = {
                nome: produto.nome.trim(),
                unidade_peso: produto.unidade_peso,
                peso_embalagem: produto.peso_embalagem !== '' ? parseFloat(String(produto.peso_embalagem).replace(',', '.')) : null,
                observacoes: produto.observacoes || null,
                controla_estoque: controla,
            };
            // So manda o custo quando ele ainda e digitado. Mandar sempre era o
            // bug: editar o codigo de barras reescrevia a media ponderada que as
            // compras apuraram.
            if (!(controla && produtoEditado?.ultima_atualizacao_custo)) {
                dados.custo_medio = produto.custo_medio !== '' ? parseFloat(produto.custo_medio) : null;
            }
            if (controla) {
                dados.codigo_barras = (produto.codigo_barras || '').trim();
                dados.codigo_barras_alternativo = (produto.codigo_barras_alternativo || '').trim();
                dados.estoque_minimo = parseInt(produto.estoque_minimo) || 0;
                dados.lead_time_dias = parseInt(produto.lead_time_dias) || 0;
                if (!produtoEditado) dados.estoque_inicial = parseFloat(produto.estoque_inicial) || 0;
            }
            if (produtoEditado) {
                await api.put(`/produtos/${produto.id}`, dados);
            } else {
                await api.post('/produtos', dados);
            }
            fecharModal();
        } catch (error) {
            setErro(error.response?.data?.erro || 'Erro ao salvar produto.');
        }
    };

    const controla = !!produto.controla_estoque;

    // Espelha `custo_e_calculado` do backend: o custo so e digitavel enquanto
    // nenhuma compra o calculou. Depende do estado ATUAL do formulario, nao do
    // que estava salvo — trocar para "so custo" aqui devolve o campo na hora,
    // que e o mesmo instante em que o servidor volta a aceitar o valor.
    const custoCalculado = controla && Boolean(produtoEditado?.ultima_atualizacao_custo);

    const custoEmReais = (v) => {
        const n = Number(v);
        return Number.isFinite(n)
            ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            : '—';
    };

    return (
        <div style={t.overlay} className="modal-overlay-mobile">
            <div style={{ ...t.modal, maxWidth: 480 }} className="modal-content-sheet">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 12, background: theme.colors.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FaBox style={{ color: theme.colors.primary, fontSize: 18 }} />
                        </div>
                        <div>
                            <h3 style={{ ...t.h2, margin: 0 }}>{produtoEditado ? 'Editar Ingrediente' : 'Novo Ingrediente'}</h3>
                            <p style={{ ...t.subtitle, margin: '2px 0 0' }}>Preencha os dados do ingrediente</p>
                        </div>
                    </div>
                    <button onClick={fecharModal} style={{ ...t.btnIcon, color: theme.colors.textMuted }}><FaTimes /></button>
                </div>

                {erro && <div style={t.error}>{erro}</div>}

                {!produtoEditado && (
                    <button onClick={() => setMostrarModalImportacao(true)}
                        style={{ ...t.btnSecondary, width: '100%', justifyContent: 'center', marginBottom: 20 }}>
                        <FaFileImport /> Importar Ingredientes via Excel
                    </button>
                )}

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <label style={t.label}>Nome do Ingrediente *</label>
                        <input name="nome" value={produto.nome} onChange={handleChange} autoComplete="off" required style={t.input} placeholder="Ex: Farinha de trigo" />
                    </div>

                    <div>
                        <label style={t.label}>Custo médio {custoCalculado ? '' : '(R$)'}</label>
                        {custoCalculado ? (
                            /* Resultado, nao campo: cada entrada de estoque recalcula a
                               media ponderada. Como input, ele reenviava o valor em toda
                               edicao e gravava por cima do que as compras apuraram — dava
                               para perder o custo do ingrediente so de corrigir o codigo
                               de barras. */
                            <div style={{ ...t.input, display: 'flex', alignItems: 'baseline', gap: 8, background: theme.colors.background, borderStyle: 'dashed' }}>
                                <strong style={{ fontSize: 16 }}>{custoEmReais(produto.custo_medio)}</strong>
                                <span style={{ fontSize: 12, color: theme.colors.textSecondary }}>
                                    calculado pelas suas entradas
                                </span>
                            </div>
                        ) : (
                            <input type="number" step="0.01" name="custo_medio" value={produto.custo_medio} onChange={handleChange} autoComplete="off" min="0" style={t.input} placeholder="Ex: 12.50" />
                        )}
                        {custoCalculado && (
                            <small style={{ fontSize: 12, color: theme.colors.textSecondary, display: 'block', marginTop: 4 }}>
                                Para mudar, registre a compra pelo preço novo — a média se ajusta sozinha.
                            </small>
                        )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                        <div>
                            <label style={t.label}>Unidade de Medida {controla ? '*' : ''}</label>
                            <select name="unidade_peso" value={produto.unidade_peso} onChange={handleChange} required={controla} style={t.select}>
                                <option value="kg">Quilograma (kg)</option>
                                <option value="l">Litro (l)</option>
                                <option value="unidade">Unidade</option>
                                <option value="caixa">Caixa</option>
                            </select>
                        </div>
                        <div>
                            <label style={t.label}>Peso/volume da embalagem {controla ? '*' : ''}</label>
                            <input
                                name="peso_embalagem"
                                value={produto.peso_embalagem}
                                onChange={(e) => setProduto({ ...produto, peso_embalagem: formatPesoEmbalagemInput(e.target.value) })}
                                autoComplete="off"
                                inputMode="numeric"
                                required={controla}
                                style={t.input}
                                placeholder="0,000" />
                        </div>
                    </div>

                    {controla && (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                                <div>
                                    <label style={t.label}>Código de Barras</label>
                                    <input name="codigo_barras" value={produto.codigo_barras || ''} onChange={handleChange} autoComplete="off" placeholder="Opcional" style={t.input} />
                                </div>
                                <div>
                                    <label style={t.label}>Código Alternativo</label>
                                    <input name="codigo_barras_alternativo" value={produto.codigo_barras_alternativo || ''} onChange={handleChange} autoComplete="off" style={t.input} />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                                <div>
                                    <label style={{ ...t.label, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        Estoque Mínimo *
                                        <Tooltip text="Quantidade mínima a manter em estoque. Quando o estoque atingir esse limite, você recebe um alerta para repor.">
                                            <span
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    width: 16, height: 16, borderRadius: '50%',
                                                    background: theme.colors.bgSection || '#F3F4F6',
                                                    color: theme.colors.textMuted,
                                                    fontSize: 10, fontWeight: 700, cursor: 'help', userSelect: 'none',
                                                }}
                                            >?</span>
                                        </Tooltip>
                                    </label>
                                    <input type="number" name="estoque_minimo" value={produto.estoque_minimo} onChange={handleChange} autoComplete="off" required min="0" style={t.input} />
                                </div>
                                {!produtoEditado && (
                                    <div>
                                        <label style={t.label}>Estoque inicial</label>
                                        <input type="number" step="0.01" name="estoque_inicial" value={produto.estoque_inicial} onChange={handleChange} autoComplete="off" min="0" style={t.input} placeholder="Ex: 5" />
                                    </div>
                                )}
                            </div>

                            <div>
                                <label style={t.label}>Lead Time (dias de reposição)</label>
                                <input type="number" name="lead_time_dias" value={produto.lead_time_dias} onChange={handleChange} autoComplete="off" min="0" style={t.input} />
                                <p style={{ fontSize: 12, color: theme.colors.textMuted, margin: '4px 0 0' }}>
                                    Tempo estimado para o fornecedor entregar
                                </p>
                            </div>
                        </>
                    )}

                    <div>
                        <label style={t.label}>Como você quer usar este ingrediente?</label>
                        <div className="form-grid-2" style={{ marginTop: 4, gap: 8 }}>
                            <button type="button" onClick={() => setProduto({ ...produto, controla_estoque: false })}
                                style={{
                                    cursor: 'pointer', textAlign: 'left',
                                    padding: '12px 14px', borderRadius: 12,
                                    border: `1.5px solid ${!controla ? theme.colors.primary : theme.colors.border}`,
                                    background: !controla ? theme.colors.primaryLight : '#fff',
                                    transition: 'all 0.15s',
                                }}>
                                <div style={{ fontSize: 18, marginBottom: 4 }}>🏷️</div>
                                <div style={{ fontWeight: 600, fontSize: 14, color: !controla ? theme.colors.primary : theme.colors.text }}>
                                    Só custo (sem controle de estoque)
                                </div>
                                <div style={{ fontSize: 11, color: theme.colors.textMuted, marginTop: 2, lineHeight: 1.3 }}>
                                    Usa o custo nas fichas técnicas, sem rastrear quantidades. Bom para itens que você sempre tem (sal, açúcar).
                                </div>
                            </button>
                            <button type="button" onClick={() => setProduto({ ...produto, controla_estoque: true })}
                                style={{
                                    cursor: 'pointer', textAlign: 'left',
                                    padding: '12px 14px', borderRadius: 12,
                                    border: `1.5px solid ${controla ? theme.colors.primary : theme.colors.border}`,
                                    background: controla ? theme.colors.primaryLight : '#fff',
                                    transition: 'all 0.15s',
                                }}>
                                <div style={{ fontSize: 18, marginBottom: 4 }}>📦</div>
                                <div style={{ fontWeight: 600, fontSize: 14, color: controla ? theme.colors.primary : theme.colors.text }}>
                                    Custo + controle de estoque
                                </div>
                                <div style={{ fontSize: 11, color: theme.colors.textMuted, marginTop: 2, lineHeight: 1.3 }}>
                                    Tudo do modo anterior + lotes, validade, código de barras e alertas de estoque baixo.
                                </div>
                            </button>
                        </div>
                    </div>

                    <div>
                        <label style={t.label}>Observações</label>
                        <textarea name="observacoes" value={produto.observacoes || ''} onChange={handleChange} rows="2" style={{ ...t.input, resize: 'vertical' }} />
                    </div>

                    <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                        <button type="submit" style={{ ...t.btnPrimary, flex: 1, justifyContent: 'center' }}>
                            {produtoEditado ? 'Salvar Alterações' : 'Cadastrar Ingrediente'}
                        </button>
                        <button type="button" onClick={fecharModal} style={{ ...t.btnSecondary, flex: 0 }}>
                            Cancelar
                        </button>
                    </div>
                </form>

                {mostrarModalImportacao && (
                    <div style={t.overlay}>
                        <div style={{ ...t.modal, maxWidth: 440 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <h3 style={t.h3}>Importar Ingredientes</h3>
                                <button onClick={() => setMostrarModalImportacao(false)} style={{ ...t.btnIcon, color: theme.colors.textMuted }}><FaTimes /></button>
                            </div>
                            <ProductImport fecharModal={() => setMostrarModalImportacao(false)} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductForm;
